import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable6 = ({ data }) => {
  const pdfRef = useRef();

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  // --- Color palette (Utah tones) ---
  const palette = [
    "#FFA1B5",
    "#86C7F3",
    "#FFE29A",
    "#93D9D9",
    "#C2A3FF",
    "#FFC58C",
    "#B9D2AA",
    "#6794D1",
  ];

  // ---- Compute Summary Metrics ----
  const totals = years.map((year) =>
    data.reduce((sum, row) => sum + Number(row[`year_${year}`]), 0),
  );

  const peakValue = Math.max(...totals);
  const peakYear = years[totals.indexOf(peakValue)];

  const growthPercent = (
    ((totals.at(-1) - totals[0]) / totals[0]) *
    100
  ).toFixed(1);

  const topAgeGroup = data.reduce(
    (max, group) => {
      const sum = years.reduce((s, y) => s + Number(group[`year_${y}`]), 0);
      return sum > max.sum ? { age: group.agegrp_label, sum } : max;
    },
    { age: null, sum: 0 },
  );

  // ---- Chart Dataset ----
  const datasets = data.map((group, i) => ({
    label: group.agegrp_label,
    data: years.map((year) => parseInt(group[`year_${year}`])),
    borderColor: palette[i % palette.length],
    backgroundColor: palette[i % palette.length] + "30",
    borderWidth: 3,
    tension: 0.4,
    pointBackgroundColor: "white",
    pointBorderColor: palette[i % palette.length],
    pointBorderWidth: 2,
    pointRadius: 5,
    fill: true,
  }));

  const chartData = { labels: years, datasets };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Age-based Workforce Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ---- PDF Export ----
const downloadPDF = () => {
  const input = pdfRef.current;
  html2canvas(input, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    const addHeader = () => {
      const logoWidth = 20; // ideal header width
      const logoHeight = 20; // adjust ratio if needed

      pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
      pdf.line(10, 35, 200, 35);
    };

    const addFooter = (pageNumber) => {
      pdf.setFontSize(9);
      const footerY = pageHeight - 10;
      pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        10,
        footerY,
      );
      pdf.text(
        `Page ${pageNumber}`,
        pageWidth - 10,
        footerY,
        null,
        null,
        "right",
      );
    };

    // First page
    addHeader();
    pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
    heightLeft -= pageHeight - 32;

    // Add more pages if needed
    while (heightLeft > 0) {
      pdf.addPage();
      pageNumber++;
      addHeader();
      position = heightLeft - imgHeight;
      pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);
    }

    // Final footer
    // ===== FINAL FOOTER SAFE AREA =====

    const footerTopY = pageHeight - 40; // start footer 40mm from bottom
    const footerContentY = pageHeight - 28; // logos row
    const footerBottomTextY = pageHeight - 10; // bottom text row

    // Top Divider Line
    pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

    // UtahData logo
    const utahWidth = 18;
    const utahHeight = 18;
    pdf.addImage(
      utahData,
      "PNG",
      20,
      footerContentY - 8,
      utahWidth,
      utahHeight,
    );

    // Built by
    pdf.setFontSize(10);
    pdf.text("Built by", 45, footerContentY);

    // BODEX logo
    const bodexWidth = 36;
    const bodexHeight = bodexWidth * 0.3;
    pdf.addImage(bodex, "PNG", 60, footerContentY - 6, bodexWidth, bodexHeight);

    // Using text
    pdf.text("using", 102, footerContentY);

    // KPI logo
    pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

    // Version
    pdf.text(APP_VERSION, 140, footerContentY);

    // Bottom Info Line
    pdf.setFontSize(8);
    pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
    pdf.text(
      `Downloaded on: ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      footerBottomTextY,
      null,
      null,
      "right",
    );

    pdf.save("employment_by_agegroup.pdf");
  });
};

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Workforce Distribution by Age Group (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-2xl font-bold text-blue-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-orange-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Workforce Year
            </p>
            <p className="text-2xl font-bold text-orange-800">{peakYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">Top Age Segment</p>
            <p className="text-xl font-bold text-green-800">
              {topAgeGroup.age}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Age Group</th>
                    {years.map((year) => (
                      <th key={year} className="p-2">
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.agegrp_label} className="hover:bg-gray-100">
                      <td className="font-medium p-2">{row.agegrp_label}</td>
                      {years.map((year) => (
                        <td key={year} className="p-2">
                          {Number(row[`year_${year}`]).toLocaleString("en-US")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Export Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} className="w-[1.5rem]" alt="Download icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable6;

